import ReactEcharts from 'echarts-for-react';
import { useEffect, useRef, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import {
  ChartControlsProps,
  ChartControlStateProps,
} from '../../redux/ChartPoperties/ChartControlsInterface';
import {
  formatChartLabelValueForSelectedMeasure,
  formatChartXAxisValue,
  formatChartYAxisValue,
} from '../ChartOptions/Format/NumberFormatter';
import { ChartsReduxStateProps } from './ChartsCommonInterfaces';
import {
  checkUidPresenceInCheckedMeasureForTooltip,
  limitChartData,
  shouldChartDisplayWithSecurity,
} from '../CommonFunctions/CommonFunctions';
import { palette } from '../..';
import EmptyQuery from './EmptyQuery';
import { RootState } from '../../redux';
import { updateHoveredItems } from '../../redux/TabTile/TabActions';
import { tooltipStyle } from '../Common/EChartTooltip';
import useCombinedColorSchemes from '../../hooks/useCombinedColorSchemes';
import {
  toggleMeasureAsBar,
  updateAxisOptions,
  updateSeriesInAxisOptions,
} from '../../redux/ChartPoperties/ChartControlsActions';
import { messages } from '../CommonFunctions/aliases';
import { toast } from 'react-toastify';
import {
  AXIS_THRESHOLDS,
  LABEL_THRESHOLDS,
  LEGEND_THRESHOLDS,
  MARGIN_THRESHOLDS,
} from '../ChartOptions/ChartThresholds';

const LineChart = ({
  //props
  propKey,
  graphDimension,
  chartArea,
  graphTileSize,
  colorScheme,
  softUI,
  fontFamily,

  //state
  chartControls,
  chartProperties,
}: ChartsReduxStateProps) => {
  const {
    selectedMeasureName,
    tabState,
    selectedTab,
    showDash,
    tooltipFormats,
    chartAxes,
    isTooltipEnabled,
    chartType,
    barLineSettings,
    yAxisOptions,
  } = useSelector((state: RootState) => ({
    selectedMeasureName:
      state.chartControls?.properties[propKey]?.formatOptions?.dateFormats?.selectedMeasureName,
    tooltipFormats: state.chartControls?.properties[propKey]?.formatOptions?.tooltipFormats,
    chartAxes: state.chartProperties?.properties[propKey]?.chartAxes,
    isTooltipEnabled:
      state.chartControls?.properties[propKey]?.formatOptions?.tooltipFormats?.isEnable,
    tabState: state.tabState,
    selectedTab: state.tabTileProps.selectedTabId,
    showDash: state.tabTileProps.showDash,
    chartType: state.chartProperties?.properties[propKey]?.chartType,
    barLineSettings: state.chartControls?.properties[propKey]?.barLineSettings || {
      barMeasures: [],
    },
    yAxisOptions: state.chartControls.properties[propKey]?.axisOptions.yAxis,
  }));

  const chartControl: ChartControlsProps = chartControls.properties[propKey];

  const currentControls = chartControl.lineChartControls || {};
  const { manualColor, nodeValuesColors, allSeriesColor } = currentControls;

  let chartData: any[] = chartControl.chartData ? chartControl.chartData : [];

  const chartRef = useRef<any>(null);
  const chartInstanceRef = useRef<echarts.ECharts | null>(null);
  const dispatch = useDispatch();

  const [seriesData, setSeriesData] = useState<any[]>([]);

  const [chartDataKeys, setChartDataKeys] = useState<string[]>([]);

  const mouseOverInteractivity =
    tabState.tabs[selectedTab]?.interactivity?.mouseOverInteractivity || false;

  const limitedChartData = limitChartData(chartData, 200, chartAxes[3]?.fields[0]?.displayname);

  const processedChartData = limitedChartData.map((item) => {
    return Object.fromEntries(
      Object.entries(item).map(([key, value]) => [
        key,
        value === null
          ? '(Blank)'
          : typeof value === 'boolean'
            ? value
              ? 'True'
              : 'False'
            : value,
      ]),
    );
  });

  const xAxisName = (processedChartData[0] && Object.keys(processedChartData[0])[0]) || '';

  useEffect(() => {
    if (chartData.length >= 1) {
      if (typeof chartData === 'object' && chartData.length > 0) {
        setChartDataKeys(Object.keys(chartData[0]));
      }
    }
  }, [chartData]);

  useEffect(() => {
    if (chartData.length > 200 && !showDash) {
      toast.warning(`${messages.chart.excessiveData} 200 records`, {
        position: 'top-center',
        autoClose: 10000,
        toastId: 'chart-data-limit-warning',
        style: {
          top: '10px',
        },
      });
    }
  }, [chartData]);

  const allThemes = useCombinedColorSchemes();
  const chartThemes = allThemes.filter((el) => {
    if (!chartControl.colorScheme || chartArea === 'dashboard') {
      return el.name === colorScheme;
    } else {
      return el.name === chartControl.colorScheme;
    }
  });
  const selectedTheme = chartThemes.length > 0 ? chartThemes[0] : allThemes[0];

  const getTopMarginForLegend = () => {
    var top = '';
    if (chartControl.legendOptions?.position?.top === 'top') {
      top = 'top';
      // } else if (chartControl.legendOptions?.position?.top === "bottom") {
      //   top = "90%";
    } else {
      top = '50%';
    }
    return top;
  };

  const [hoveredColumns, setHoveredColumns] = useState<Record<string, any>[]>([]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (
        !mouseOverInteractivity ||
        (tabState.tabs[selectedTab].hoveredPropkey &&
          tabState.tabs[selectedTab].hoveredPropkey === propKey)
      ) {
        return;
      }
      const chartInstance = chartInstanceRef.current;
      if (!chartInstance || chartInstance.isDisposed?.()) return;

      const index = processedChartData.findIndex((item) =>
        tabState.tabs[selectedTab].hoveredItems?.some(
          (hovered: Record<string, any>) =>
            String(hovered[chartDataKeys[0]]) === String(item[chartDataKeys[0]]),
        ),
      );

      if (index >= 0) {
        for (let i = 0; i < seriesData.length; i++) {
          chartInstance.dispatchAction({
            type: 'highlight',
            seriesIndex: i,
            dataIndex: index,
            blurOthers: true,
          });
        }
      } else {
        for (let i = 0; i < seriesData.length; i++) {
          chartInstance.dispatchAction({
            type: 'downplay',
            seriesIndex: i,
          });
        }
      }
    }, 30); // slight delay helps when charts update

    return () => clearTimeout(timeout);
  }, [tabState.tabs[selectedTab].hoveredItems, chartData]);

  const hoveredRef = useRef<Record<string, any> | null>(null);

  const hoverdTimeout = useRef<NodeJS.Timeout | null>(null);

  const handleChartReady = (chartInstance: any) => {
    if (!mouseOverInteractivity) return;

    chartInstanceRef.current = chartInstance;

    chartInstance.on('mouseover', (params: any) => {
      if (params?.name && hoveredRef.current?.[chartDataKeys[0]] !== params.name) {
        if (hoverdTimeout.current) clearTimeout(hoverdTimeout.current);

        hoverdTimeout.current = setTimeout(() => {
          const newEntry = { [chartDataKeys[0]]: params.name };
          hoveredRef.current = newEntry;

          setHoveredColumns((prev) => {
            const isDuplicate = prev.some((item) => item[chartDataKeys[0]] === params.name);
            if (!isDuplicate) {
              return [...prev, newEntry];
            }
            return prev;
          });
        }, 200);
      }
    });

    chartInstance.on('mouseout', (params: any) => {
      if (hoverdTimeout.current) clearTimeout(hoverdTimeout.current);
      if (params?.name) {
        hoveredRef.current = null;
        setHoveredColumns([]);
      }
    });
  };

  const shouldRenderAsBar = (measureName: string) => {
    const measureAxis = chartAxes.find((axis: any) => axis.name === 'Measure');
    if (!measureAxis || !barLineSettings?.barMeasures?.length) return false;
    const measureField = measureAxis.fields?.find(
      (field: any) => field.displayname === measureName,
    );
    if (measureField && barLineSettings.barMeasures.includes(measureField.uId)) {
      return true;
    }
    return false;
  };

  //------ Auto-remove excess series when measures are removed ------------
  useEffect(() => {
    const seriesList = Object.keys(yAxisOptions);
    const measureFields = chartAxes.find((axis: any) => axis.name === 'Measure')?.fields;
    const maxSeriesAllowed = Math.min(measureFields.length, 4);
    if (measureFields?.length > 0 && seriesList.length > maxSeriesAllowed) {
      const lastSeriesNumber = seriesList[seriesList.length - 1];
      //1-Move removed series measure into series 1, if that measure is not removed
      const lastSeriesMeasureList: string[] = yAxisOptions[lastSeriesNumber].measuresList || [];
      lastSeriesMeasureList.forEach((measureUid) => {
        // check whether that measure UID is present in chartAxes measure
        if (measureFields.some((measure: any) => measure.uId === measureUid)) {
          const updatedList = [...yAxisOptions['1'].measuresList];
          updatedList.push(measureUid);
          dispatch(updateAxisOptions(propKey, 'yAxis', 'measuresList', updatedList, '1'));
        }
      });
      //2-remove series
      dispatch(updateSeriesInAxisOptions(propKey, lastSeriesNumber, 'remove', 'y'));
    }
  }, [chartAxes, yAxisOptions, propKey]);

  const buildYAxes = () => {
    const allSeries = Object.keys(yAxisOptions);
    const offsetStep = 60; // Space between multiple Y-axes on the same side

    // Filter out series with no measures
    const seriesWithMeasures = allSeries.filter((series: string) => {
      const measuresList = yAxisOptions[series].measuresList || [];
      return measuresList.length > 0;
    });

    return seriesWithMeasures.map((series: string, index: number) => {
      const position = yAxisOptions[series].position; // 'left' or 'right'

      // Count how many previous series are on the same side
      const sameSideCount = seriesWithMeasures
        .slice(0, index)
        .filter((s) => yAxisOptions[s].position === position).length;
      const offset = sameSideCount * offsetStep;

      return buildSingleYAxis(series, offset);
    });
  };

  const yAxisIndexForMeasure = (measureName: string) => {
    let allSeries = Object.keys(yAxisOptions);

    // Filter to only include series with measures
    const seriesWithMeasures = allSeries.filter((series: string) => {
      const measuresList = yAxisOptions[series].measuresList || [];
      return measuresList.length > 0;
    });

    const measureAxisField = chartAxes.find((axis: any) => axis.name === 'Measure').fields;
    const measureField = measureAxisField.find((field: any) => field.displayname === measureName);
    const measureUid = measureField?.uId;
    const key =
      seriesWithMeasures.find((key: any) => yAxisOptions[key].measuresList?.includes(measureUid)) ||
      '1';
    const index = seriesWithMeasures.indexOf(key);
    return index;
  };

  const buildSingleYAxis = (seriesNumber: string, offset: number = 0) => {
    const position = yAxisOptions[seriesNumber].position;
    return {
      type: 'value',
      inverse: chartControl.axisOptions.inverse,

      position: position,
      offset: offset, // Apply dynamic offset based on axis position

      show: true,

      name:
        Math.floor(graphDimension.height) > AXIS_THRESHOLDS.common.h
          ? yAxisOptions[seriesNumber].name
          : '',
      nameLocation: yAxisOptions[seriesNumber].nameLocation,
      nameGap: yAxisOptions[seriesNumber].nameGap,
      nameTextStyle: {
        fontSize: yAxisOptions[seriesNumber].nameSize,
        color: yAxisOptions[seriesNumber].nameColor,
      },

      axisLine: {
        show:
          yAxisOptions[seriesNumber].position !== 'hide' &&
          Math.floor(graphDimension.height) > AXIS_THRESHOLDS.common.h,
        onZero: yAxisOptions[seriesNumber].onZero,
      },

      min: yAxisOptions[seriesNumber].enableMin ? yAxisOptions[seriesNumber].minValue : null,
      max: yAxisOptions[seriesNumber].enableMax ? yAxisOptions[seriesNumber].maxValue : null,

      axisTick: {
        show:
          yAxisOptions[seriesNumber].position !== 'hide' &&
          Math.floor(graphDimension.height) > AXIS_THRESHOLDS.common.h,
        alignWithLabel: true,
        length:
          yAxisOptions[seriesNumber].position === 'left'
            ? yAxisOptions[seriesNumber].tickSizeLeft
            : yAxisOptions[seriesNumber].tickSizeRight,
      },

      axisLabel: {
        show:
          yAxisOptions[seriesNumber].position !== 'hide' &&
          Math.floor(graphDimension.height) > AXIS_THRESHOLDS.common.h,
        fontSize: chartControl.axisOptions.yAxis[1].fontSize,
        rotate:
          yAxisOptions[seriesNumber].position === 'left'
            ? yAxisOptions[seriesNumber].tickRotationLeft
            : yAxisOptions[seriesNumber].tickRotationRight,
        margin:
          yAxisOptions[seriesNumber].position === 'left'
            ? yAxisOptions[seriesNumber].tickPaddingLeft
            : yAxisOptions[seriesNumber].tickPaddingRight,
        color: selectedTheme?.dark ? '#ffffff' : palette.primary.contrastText,
        fontFamily: fontFamily,
        formatter: (value: number) => {
          var formattedValue = formatChartYAxisValue(chartControl, value, undefined, seriesNumber);
          return formattedValue;
        },
      },

      splitLine: {
        show: chartControl.axisOptions.ySplitLine,
        lineStyle: {
          color: yAxisOptions[seriesNumber]?.splitLineColor || '#d3d3d3',
          width: yAxisOptions[seriesNumber]?.splitLineWidth ?? 1,
          type: yAxisOptions[seriesNumber]?.splitLineType || 'solid',
        },
      },
    };
  };

  const seriesDataFn = () => {
    if (!processedChartData.length) return [];

    const keys = Object.keys(processedChartData[0]);
    const xKey = keys[0];
    const hasSplitBy = chartProperties.properties[propKey].chartAxes[2]?.fields?.length > 0;

    const getAxisSettingsForMeasure = (measureDisplayName: string) => {
      //   UID of the measure
      const measureAxisField = chartAxes.find((axis: any) => axis.name === 'Measure')?.fields;
      const measureField = measureAxisField?.find(
        (field: any) => field.displayname === measureDisplayName,
      );
      const measureUid = measureField?.uId;

      const allSeries = Object.keys(yAxisOptions);
      // Default to '1' if not found
      const axisKey =
        allSeries.find((key) => yAxisOptions[key].measuresList?.includes(measureUid)) || '1';

      return yAxisOptions[axisKey];
    };

    // --- Clamping Function using specific axis settings ---
    const getClampedValue = (val: any, axisSettings: any) => {
      if (val === null || val === undefined || isNaN(val)) return val;
      let finalVal = val;

      if (!axisSettings) return val;

      // Cap at Max
      if (axisSettings.enableMax && axisSettings.maxValue !== null && val > axisSettings.maxValue) {
        finalVal = axisSettings.maxValue;
      }
      // Floor at Min
      if (axisSettings.enableMin && axisSettings.minValue !== null && val < axisSettings.minValue) {
        finalVal = axisSettings.minValue;
      }
      return finalVal;
    };

    const buildLabel = (yKey: string) => ({
      show:
        Math.floor(graphDimension.height) > LABEL_THRESHOLDS.common.h
          ? chartControl.labelOptions.showLabel
          : false,
      fontSize: chartControl.labelOptions.fontSize,
      fontFamily: fontFamily,
      color: chartControl.labelOptions.labelColor
        ? chartControl.labelOptions.labelColor
        : selectedTheme?.dark
          ? '#ffffff'
          : '#757474',
      backgroundColor: chartControl.labelOptions.labelBackground
        ? chartControl.labelOptions.labelBackgroundColor
        : 'transparent',
      padding: chartControl.labelOptions.labelBackground ? [4, 8] : 0,
      borderRadius: chartControl.labelOptions.labelBackground ? 4 : 0,
      formatter: (params: any) => {
        // Use originalValue if available, otherwise fall back to standard value
        const val = params.data?.originalValue ?? params.value?.[1] ?? params.value?.[yKey];
        return formatChartLabelValueForSelectedMeasure(
          chartControls.properties[propKey],
          chartProperties.properties[propKey],
          val,
          yKey,
        );
      },
    });

    let seriesArr: any[] = [];
    const themeColors = selectedTheme?.colors || [];

    // Helper to get color
    const getSeriesColor = (name: string, index: number) => {
      if (manualColor) {
        if (nodeValuesColors && nodeValuesColors[name]) {
          return nodeValuesColors[name];
        }
        if (allSeriesColor) {
          return allSeriesColor;
        }
      }
      return themeColors[index % themeColors.length];
    };

    if (hasSplitBy) {
      //--If split by is added, single measure is left so make bar measure as empty------------
      barLineSettings?.barMeasures?.length > 0 && dispatch(toggleMeasureAsBar(propKey, []));
      //----------------------------------------------------------------------------------------
      const splitField = chartProperties.properties[propKey].chartAxes[2]?.fields[0]?.displayname;
      const measureField = chartProperties.properties[propKey].chartAxes[3]?.fields[0]?.displayname;

      const axisSettings = getAxisSettingsForMeasure(measureField);
      const groups = [...new Set(processedChartData.map((d) => d[splitField]))];

      seriesArr = groups.map((group, i) => {
        const color = getSeriesColor(group as string, i);
        return {
          name: group,
          type: 'line',
          smooth: chartControl.smoothCurve?.enable,
          datasetIndex: 0,
          itemStyle: { color: color },
          lineStyle: {
            color: color,
            ...(softUI ? { shadowColor: 'rgba(0,0,0,0.5)', shadowBlur: 10, shadowOffsetY: 3 } : {}),
          },
          data: processedChartData
            .filter((d) => d[splitField] === group)
            .map((d) => {
              const rawVal = d[measureField];
              const plottedVal = getClampedValue(rawVal, axisSettings);
              return {
                value: [String(d[xKey]), plottedVal],
                originalValue: rawVal,
              };
            }),
          label: buildLabel(measureField),
        };
      });
    } else {
      const measureFields =
        chartProperties.properties[propKey].chartAxes[3]?.fields.map((f: any) => f.displayname) ||
        [];

      seriesArr = measureFields.map((mKey: string, i: number) => {
        const baseSeriesColor = getSeriesColor(mKey, i);
        const isBar = shouldRenderAsBar(mKey);

        const axisSettings = getAxisSettingsForMeasure(mKey);

        const data = processedChartData.map((d) => {
          const rawVal = d[mKey];
          const plottedVal = getClampedValue(rawVal, axisSettings);

          return {
            value: [String(d[xKey]), plottedVal],
            originalValue: rawVal,
          };
        });

        return {
          name: mKey,
          yAxisIndex: yAxisIndexForMeasure(mKey),
          type: isBar ? 'bar' : 'line',
          smooth: chartControl.smoothCurve?.enable,
          // encode: { x: xKey, y: mKey }, // Removed encoded to allow manual data mapping
          datasetIndex: 0,

          itemStyle: { color: baseSeriesColor },
          lineStyle: {
            color: baseSeriesColor,
            ...(softUI ? { shadowColor: 'rgba(0,0,0,0.5)', shadowBlur: 10, shadowOffsetY: 3 } : {}),
          },
          data: data,
          label: buildLabel(mKey),
        };
      });
    }

    return seriesArr;
  };
  useEffect(() => {
    dispatch(updateHoveredItems(selectedTab, hoveredColumns, propKey));
  }, [hoveredColumns]);

  const chartInstance = chartRef.current?.getEchartsInstance?.();
  const currentOption = chartInstance?.getOption();
  const [currentLegendSelected, setCurrentLegendSelected] = useState<any>(
    currentOption?.legend?.[0]?.selected ?? {},
  );

  const onLegendSelectChanged = (params: any) => {
    setCurrentLegendSelected(params.selected);
  };

  const RenderChart = () => {
    return (
      <ReactEcharts
        ref={chartRef}
        onChartReady={handleChartReady}
        onEvents={{
          legendselectchanged: onLegendSelectChanged,
        }}
        // theme={chartControl.colorScheme}
        style={{
          // padding: "1rem",
          width: graphDimension.width,
          height: graphDimension.height,
          overflow: 'hidden',
          margin: 'auto',
          border: chartArea ? 'none' : graphTileSize ? 'none' : '1px solid rgb(238,238,238)',
        }}
        option={{
          color: selectedTheme?.colors || [],
          backgroundColor: selectedTheme?.background || [],
          // animation: chartArea ? false : true, // Turning animation off for current release, to be added later on
          animation: false,
          legend: {
            // textStyle :{color : getContrastColor(chartThemes[0].background)},
            type: 'scroll',
            show:
              Math.floor(graphDimension.height) > LEGEND_THRESHOLDS.line.h
                ? chartControl.legendOptions?.showLegend
                : false,
            itemHeight: chartControl.legendOptions?.symbolHeight,
            itemWidth: chartControl.legendOptions?.symbolWidth,
            itemGap: chartControl.legendOptions?.itemGap,

            left: chartControl.legendOptions?.position?.left,
            top:
              chartControl.legendOptions?.position?.top !== 'bottom'
                ? getTopMarginForLegend()
                : null,
            // top: chartControl.legendOptions?.position?.top,
            bottom: chartControl.legendOptions?.position?.top === 'bottom' ? 0 : null,
            orient: chartControl.legendOptions?.orientation,
            selected: currentLegendSelected,
            textStyle: {
              color: selectedTheme?.dark ? '#ffffff' : palette.primary.contrastText,
              fontFamily: fontFamily,
            },
          },
          grid: {
            left:
              Math.floor(graphDimension.height) <= MARGIN_THRESHOLDS.compact.h
                ? 5
                : chartControl.chartMargin.left + '%',
            right:
              Math.floor(graphDimension.height) <= MARGIN_THRESHOLDS.compact.h
                ? 5
                : chartControl.chartMargin.right + '%',
            top:
              Math.floor(graphDimension.height) <= MARGIN_THRESHOLDS.compact.h
                ? 5
                : chartControl.legendOptions?.position?.top === 'top'
                  ? chartControl.chartMargin.top + 5 + '%'
                  : chartControl.chartMargin.top + '%',
            bottom:
              Math.floor(graphDimension.height) <= MARGIN_THRESHOLDS.compact.h
                ? 5
                : chartControl.legendOptions?.position?.top === 'bottom'
                  ? (graphDimension.height * chartControl.chartMargin.bottom) / 100 + 35
                  : chartControl.chartMargin.bottom + '%',
            shadowColor: 'rgba(0, 0, 0, 0.5)', // Setting shadow color
            shadowBlur: 10,
            shadowOffsetX: 2,
            shadowOffsetY: 2,
          },

          tooltip: {
            show: isTooltipEnabled,
            useHTML: true,
            trigger: 'item',
            backgroundColor: 'white',
            borderWidth: 1,
            padding: 0,
            textStyle: {
              color: '#333',
              fontSize: 16,
            },
            appendToBody: true,
            extraCssText:
              'box-shadow: 0 2px 8px rgba(0,0,0,0.15);white-space: normal; word-break: break-word;',
            formatter: function (params: any) {
              //----select measure field which is hovered and dimension field(s)----//
              const hasSplitBy =
                chartProperties.properties[propKey].chartAxes[2]?.fields?.length > 0;
              let row;

              if (hasSplitBy) {
                // For split by charts, find the row based on x-axis value and series name
                const xValue = params.name || params.value?.[0];
                const splitFieldName =
                  chartProperties.properties[propKey].chartAxes[2]?.fields[0]?.displayname;
                row = processedChartData.find(
                  (item) =>
                    String(item[Object.keys(processedChartData[0])[0]]) === String(xValue) &&
                    String(item[splitFieldName]) === String(params.seriesName),
                );
              } else {
                row = processedChartData[params.dataIndex];
              }

              if (!row) return '';

              let hoveredFieldname, tempData;
              const dimensionAxis = chartAxes[1];
              const dimensionAxisDisplayNames = dimensionAxis.fields.map(
                (field: any) => field.displayname,
              );

              if (hasSplitBy) {
                // For split-by charts, use the measure field name and include split field
                const measureField =
                  chartProperties.properties[propKey].chartAxes[3]?.fields[0]?.displayname;
                const splitField =
                  chartProperties.properties[propKey].chartAxes[2]?.fields[0]?.displayname;
                hoveredFieldname = measureField;

                tempData = {
                  ...Object.fromEntries(
                    dimensionAxisDisplayNames.map((key: any) => [key, row[key]]),
                  ),
                  [splitField]: row[splitField],
                  [measureField]: row[measureField],
                };
              } else {
                // For non-split charts, use series name as hovered field
                hoveredFieldname = params.seriesName;
                const allFieldName = [...dimensionAxisDisplayNames, hoveredFieldname];
                const newData: Record<string, any> = {};
                allFieldName.map((key: string) => (newData[key] = row[key]));
                tempData = newData;
              }

              //---------get list of non hovered field -------//
              const allFieldName = hasSplitBy
                ? [
                    ...dimensionAxisDisplayNames,
                    hoveredFieldname,
                    chartProperties.properties[propKey].chartAxes[2]?.fields[0]?.displayname,
                  ]
                : [...dimensionAxisDisplayNames, hoveredFieldname];

              const nonHoveredFieldList = [
                ...new Set(
                  processedChartData
                    .map((data: any) =>
                      Object.keys(data).filter((key: string) => !allFieldName.includes(key)),
                    )
                    .flat(),
                ),
              ];
              //----------remove unchecked measure or any other field value of type date inside tooltip----------//
              const _data: { [key: string]: string | number } = {};
              Object.keys(tempData).forEach((key: string) => {
                const resp = checkUidPresenceInCheckedMeasureForTooltip(
                  chartAxes,
                  key,
                  tooltipFormats?.checkedMeasure,
                );
                if (resp) {
                  _data[key] = tempData[key];
                }
              });
              let mergedData = { ..._data };
              //-- send extra responses in _data so that it maps and shows all key value pairs including extra measure (if any)--//
              //----get extra response corresponding to all dimensions and create tooltip data----//
              let extendedDimensionNames = dimensionAxisDisplayNames;
              if (hasSplitBy) {
                const splitField =
                  chartProperties.properties[propKey].chartAxes[2]?.fields[0]?.displayname;
                extendedDimensionNames = [...dimensionAxisDisplayNames, splitField];
              }
              const extraResponses = Array.isArray(tooltipFormats?.extraMeasureFieldsResponses)
                ? tooltipFormats.extraMeasureFieldsResponses
                : [];
              if (extraResponses.length > 0) {
                const lookupKeys = extendedDimensionNames;
                const tooltipData = extraResponses.find((obj: any) =>
                  lookupKeys.every((dimensionKey: string) => {
                    const dimensionValue = tempData[dimensionKey as string];
                    return (
                      dimensionValue !== undefined &&
                      String(obj[dimensionKey as string]) === String(dimensionValue as string)
                    );
                  }),
                );

                let filteredFields = tooltipData ? { ...tooltipData } : {};
                if (filteredFields) {
                  lookupKeys.forEach((dimensionKey: string) => {
                    delete filteredFields[dimensionKey];
                  });
                }

                if (tooltipData && Object.keys(filteredFields).length > 0) {
                  const isHoveredFieldChecked = checkUidPresenceInCheckedMeasureForTooltip(
                    chartAxes,
                    hoveredFieldname,
                    tooltipFormats?.checkedMeasure,
                  );

                  if (!isHoveredFieldChecked) {
                    delete filteredFields[hoveredFieldname];
                  }

                  Object.keys(filteredFields).forEach((key) => {
                    if (
                      nonHoveredFieldList.includes(key) ||
                      !checkUidPresenceInCheckedMeasureForTooltip(
                        chartAxes,
                        key,
                        tooltipFormats?.checkedMeasure,
                      )
                    ) {
                      delete filteredFields[key];
                    }
                  });

                  mergedData = { ...mergedData, ...filteredFields };
                }
              }

              return tooltipStyle(
                params.color,
                mergedData,
                tooltipFormats,
                chartAxes,
                extendedDimensionNames,
                undefined,
                undefined,
                fontFamily,
              );
            },
          },
          dataset: {
            // dimensions: Object.keys(processedChartData[0]),
            source: processedChartData,
          },
          xAxis: {
            type: 'category',
            boundaryGap:
              Math.floor(graphDimension.height) > AXIS_THRESHOLDS.common.h ? true : false,
            position: chartControl.axisOptions.xAxis[1].position,

            axisLine: {
              show:
                chartControl.axisOptions.xAxis[1].position !== 'hide' &&
                Math.floor(graphDimension.height) > AXIS_THRESHOLDS.common.h,
              onZero:
                chartControl.axisOptions.xAxis[1].position === 'top'
                  ? false
                  : chartControl.axisOptions.xAxis[1].onZero,
            },

            axisTick: {
              show:
                chartControl.axisOptions.xAxis[1].position !== 'hide' &&
                Math.floor(graphDimension.height) > AXIS_THRESHOLDS.common.h,
              alignWithLabel: true,
              length:
                chartControl.axisOptions.xAxis[1].position === 'top'
                  ? chartControl.axisOptions.xAxis[1].tickSizeTop
                  : chartControl.axisOptions.xAxis[1].tickSizeBottom,
            },
            axisLabel: {
              show:
                chartControl.axisOptions.xAxis[1].position !== 'hide' &&
                Math.floor(graphDimension.height) > AXIS_THRESHOLDS.common.h,
              fontSize: chartControl.axisOptions.xAxis[1].fontSize,
              rotate:
                chartControl.axisOptions.xAxis[1].position === 'top'
                  ? chartControl.axisOptions.xAxis[1].tickRotationTop
                  : chartControl.axisOptions.xAxis[1].tickRotationBottom,
              margin:
                chartControl.axisOptions.xAxis[1].position === 'top'
                  ? chartControl.axisOptions.xAxis[1].tickPaddingTop
                  : chartControl.axisOptions.xAxis[1].tickPaddingBottom,
              color: selectedTheme?.dark ? '#ffffff' : palette.primary.contrastText,
              fontFamily: fontFamily,
              formatter: (value: any) => {
                const fieldInAxes = chartAxes
                  ?.flatMap((axis: any) => axis?.fields || [])
                  ?.find((f: any) => f.displayname === xAxisName);

                const measureUid = fieldInAxes?.uId;

                const labelFormat = chartControl?.formatOptions?.labelFormats?.measureFormats?.[
                  measureUid
                ] as any;

                if (labelFormat?.finalDateFormat) {
                  return formatChartXAxisValue(chartControl, value, labelFormat);
                }

                return value;
              },
            },
            splitLine: {
              show: chartControl.axisOptions.xSplitLine ?? false,
              lineStyle: {
                color: chartControl.axisOptions.xAxis[1]?.splitLineColor || '#d3d3d3',
                width: chartControl.axisOptions.xAxis[1]?.splitLineWidth ?? 1,
                type: chartControl.axisOptions.xAxis[1]?.splitLineType || 'solid',
              },
            },

            show: true,

            name:
              Math.floor(graphDimension.height) > AXIS_THRESHOLDS.common.h
                ? chartControl.axisOptions.xAxis[1].name
                : '',
            nameLocation: chartControl.axisOptions.xAxis[1].nameLocation,
            nameGap: chartControl.axisOptions.xAxis[1].nameGap,
            nameTextStyle: {
              fontSize: chartControl.axisOptions.xAxis[1].nameSize,
              color: chartControl.axisOptions.xAxis[1].nameColor,
            },
          },

          yAxis: buildYAxes(),
          series: seriesDataFn(),
        }}
      />
    );
  };

  const emptyQueryCondition =
    shouldChartDisplayWithSecurity(chartAxes, chartType, chartData) === false ||
    chartData.length < 1;

  return emptyQueryCondition ? (
    <EmptyQuery
      graphDimension={graphDimension}
      propKey={propKey}
      chartArea={chartArea}
      graphTileSize={graphTileSize}
      chartThemes={chartThemes}
      showEmptyQueryMessageWithSecurity={
        !shouldChartDisplayWithSecurity(chartAxes, chartType, chartData) ? true : false
      }
    />
  ) : (
    <RenderChart />
  );
};

const mapStateToProps = (state: ChartControlStateProps & any, ownProps: any) => {
  return {
    chartControls: state.chartControls,
    chartProperties: state.chartProperties,
  };
};

export default connect(mapStateToProps, null)(LineChart);
